// Copyright (c) Ping. All rights reserved.

import type React from 'react';
import { useCallback } from 'react';
import {
  FlatList,
  type ListRenderItem,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { createTheme } from '../design-system/theme';
import { useColorScheme } from '../foundations/colorScheme';
import { CallHistoryEmptyState } from './CallHistoryEmptyState';
import { CallHistoryRow } from './CallHistoryRow';
import { callHistoryStrings } from './strings';
import type { CallHistoryEntry, CallHistoryEntryId } from './types';

export interface CallHistoryFavoritesProps {
  readonly entries: readonly CallHistoryEntry[];
  readonly selectedEntryId?: CallHistoryEntryId | null;
  readonly onSelect?: (entry: CallHistoryEntry) => void;
  readonly onToggleFavorite?: (entry: CallHistoryEntry) => void;
}

const ROW_HEIGHT_ESTIMATE = 76;

/**
 * Favorites tab body. Renders the favorite-filtered entries; the empty
 * variant matches the design copy for the favorites filter.
 */
export const CallHistoryFavorites: React.FC<CallHistoryFavoritesProps> = ({
  entries,
  selectedEntryId = null,
  onSelect,
  onToggleFavorite,
}) => {
  const scheme = useColorScheme();
  const theme = createTheme(scheme);

  const keyExtractor = useCallback((item: CallHistoryEntry) => item.summary.id, []);

  const renderItem: ListRenderItem<CallHistoryEntry> = useCallback(
    ({ item }) => (
      <CallHistoryRow
        entry={item}
        selected={selectedEntryId === item.summary.id}
        {...(onSelect !== undefined ? { onPress: onSelect } : {})}
        {...(onToggleFavorite !== undefined ? { onToggleFavorite } : {})}
      />
    ),
    [onSelect, onToggleFavorite, selectedEntryId],
  );

  const getItemLayout = useCallback(
    (_: ArrayLike<CallHistoryEntry> | null | undefined, index: number) => ({
      length: ROW_HEIGHT_ESTIMATE,
      offset: ROW_HEIGHT_ESTIMATE * index,
      index,
    }),
    [],
  );

  if (entries.length === 0) {
    return <CallHistoryEmptyState filter="favorites" query="" />;
  }

  return (
    <View style={styles.wrap}>
      <View style={[styles.header, { borderBottomColor: theme.colors.border.secondary }]}>
        <Text style={[styles.title, { color: theme.colors.text.primary }]}>
          {callHistoryStrings.favoritesTitle}
        </Text>
      </View>
      <FlatList<CallHistoryEntry>
        data={entries}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        getItemLayout={getItemLayout}
        windowSize={10}
        initialNumToRender={15}
        maxToRenderPerBatch={10}
        removeClippedSubviews={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
  } satisfies ViewStyle,
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  title: {
    fontSize: 15,
    fontWeight: '600',
  } satisfies TextStyle,
});
